import { Box, CircularProgress, Typography } from '@mui/material';
import { createContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../AuthProvider';
import Header from '../../layout/Header';
import './template.module.css';
import { GoBack } from './components/HelperSVGs';
import TemplateTableSection from './components/TemplatesTableSection';
import { colors } from './utils/colors';
import CreateTemplateSection from './components/CreateTemplateSection';
import CustomizeTemplateSection from './components/CustomizeTemplateSection';
import ProgressIndicator from './components/ProgressIndicator';

const sectionsJson = [
  {
    // Blocks completed
    title: 'Demographics',
    key: 'demographics',

    blocks: [
      {
        title: 'Grade',
        status: false,
        outfields: ['grade'],
      },
      {
        title: 'Demographic Group',
        status: false,
        outfields: ['group'],
      },
      {
        title: 'Nearest Groups',
        status: false,
        outfields: ['nearest_groups'],
      },
      {
        title: 'Who We Are',
        status: false,
        outfields: ['who_we_are'],
      },
    ],

    status: false,
  },
  {
    // Blocks completed
    title: 'Crime',
    key: 'crime',

    blocks: [
      {
        title: 'Overall Crime Index Score',
        status: false,
        outfields: ['overall_crime_index_score'],
      },
      {
        title: 'Property Crime Index Score',
        status: false,
        outfields: ['property_crime_index_score'],
      },
    ],
    status: false,
  },
  {
    title: 'Expansion',
    key: 'expansion',

    status: false,
    blocks: [
      {
        title: 'Block',
        status: false,
        outfields: ['block'],
      },
      {
        title: 'Tract',
        status: false,
        outfields: ['tract'],
      },
      {
        title: 'Zip',
        status: false,
        outfields: ['zip'],
      },
      {
        title: 'County',
        status: false,
        outfields: ['county'],
      },
      {
        title: 'State',
        status: false,
        outfields: ['state'],
      },
      {
        title: 'National',
        status: false,
        outfields: ['national'],
      },
    ],
  },
  {
    title: 'Income & Spending',
    key: 'income',

    status: false,
    blocks: [
      // Top Row with 6 inner blocks
      {
        title: 'Median Household Income',
        status: false,
        outfields: ['median_household_income'],
      },
      // Second Row with 3 inner blocks
      {
        title: 'Median Home Value',
        status: false,
        outfields: ['median_home_value'],
      },
      // Last Row with 2 inner blocks
      {
        title: 'Annual Forecasted Median Income Growth',
        status: false,
        outfields: ['annual_forecasted_median_income_growth'],
      },
    ],
  },
  {
    title: 'Rent',
    key: 'rent',

    status: false,
    blocks: [
      {
        title: 'Average',
        status: false,
        outfields: ['average'],
      },
      {
        title: 'Median',
        status: false,
        outfields: ['median'],
      },
      {
        title: '25 ~ Percentile',
        status: false,
        outfields: ['percentile_25'],
      },
      {
        title: '75 ~ Percentile',
        status: false,
        outfields: ['percentile_75'],
      },
    ],
  },
  {
    title: 'Neighborhood',
    key: 'neighborhood',

    status: false,
    blocks: [
      {
        title: 'Nearest Stores',
        status: false,
        outfields: ['StoreName'],
      },
    ],
  },
  {
    title: 'House Price Index',
    key: 'hpi',

    status: false,
    blocks: [
      {
        // For top row
        title: 'Price Appreciation Since 2011',
        status: false,
        outfields: ['price_appreciation_since_2011'],
      },
      {
        // For bottom row
        title: 'Background Rate of Appreciation (1990 - present)',
        status: false,
        outfields: ['background_rate_of_appreciation'],
      },
      {
        // For the chart
        title: 'HPI TRACT/ZIP INDEX',
        status: false,
        outfields: ['hpi_tract_zip_index'],
      },
    ],
  },
  {
    title: 'MSA',
    key: 'msa',

    status: false,
    blocks: [
      {
        title: 'Stat Block',
        status: false,
        outfields: ['stat_block'],
      },
      {
        title: 'Business Diversification Index',
        status: false,
        outfields: ['business_diversification_index'],
      },
      {
        title: 'Employers Diversification Index',
        status: false,
        outfields: ['employers_diversification_index'],
      },
      {
        title: 'Top 5 Businesses by Sector',
        status: false,
        outfields: ['top_businesses'],
      },
      {
        title: 'Top 5 Employers by Sector',
        status: false,
        outfields: ['top_employers'],
      },
    ],
  },
  {
    title: 'Schools',
    key: 'schools',

    status: false,
    blocks: [
      {
        title: 'School District Name',
        status: false,
        outfields: ['district_name'],
      },
      {
        title: 'Nearest 3 Schools',
        status: false,
        outfields: ['nearest_3_schools'],
      },
    ],
  },
];

export const ReportTemplatesContext = createContext();

export default function ReportTemplates() {
  const navigate = useNavigate();
  const { getAccessToken, user, mongoUser, isLoading } = useAuth();

  const [adminLoader, setAdminLoader] = useState(true);
  const [loading, setLoading] = useState(false);
  const [arcgisToken, setArcgisToken] = useState(null);
  const [editing, setEditing] = useState(false);
  const [templateID, setTemplateID] = useState(null);
  const [sideBarState, setSideBarState] = useState(false);
  const [currentStage, setCurrentStage] = useState('templateTable');
  const [newTemplateName, setNewTemplateName] = useState('');
  const [lastStage, setLastStage] = useState('createTemplate');
  const [addBlockModal, setAddBlockModal] = useState({
    status: false,
    callingSection: null,
  });
  const [Sections, setSections] = useState(() =>
    JSON.parse(JSON.stringify(sectionsJson)),
  );

  const [Maps, setMaps] = useState(null);
  const [customMapData, setCustomMapData] = useState({});

  // * Helper Functions
  function removeBlock(blockName, sectionKey, groupKey = null) {
    const updatedSections = Sections.map(section => {
      if (section.key === sectionKey) {
        section.blocks = section.blocks.map(block => {
          if (block.title === blockName && block.group === groupKey) {
            block.status = false;
          }
          return block;
        });
      }
      return section;
    });

    setSections(updatedSections);
  }

  function addCustomMapInfo(sectionKey, selectedBlock) {
    const updatedSections = Sections.map(section => {
      if (section.key === sectionKey) {
        section.mapBlock = selectedBlock;
        section.title = selectedBlock.title + ` (${selectedBlock.group})`;
        return section;
      }
      return section;
    });

    setSections(updatedSections);
  }

  function handleSaveCustomMap(customMapData) {
    const { sectionKey, mapSettings, legendSettings, selectedBlock, webmapID } =
      customMapData;

    // Store the custom map data
    setCustomMapData(prev => ({
      ...prev,
      [sectionKey]: {
        mapSettings,
        legendSettings,
        selectedBlock,
        webmapID,
        timestamp: new Date().toISOString(),
      },
    }));

    // Update the section with custom map configuration
    const updatedSections = Sections.map(section => {
      if (section.key === sectionKey) {
        return {
          ...section,
          mapBlock: {
            ...section.mapBlock,
            customMapConfig: {
              type: 'custom',
              mapSettings,
              legendSettings,
              selectedBlock,
            },
          },
        };
      }
      return section;
    });

    setSections(updatedSections);

    // Show success message

    // You could add a toast notification here if you have a notification system
    // For now, we'll just log it
  }

  function updateBlockTitle(oldTitle, newTitle, sectionKey) {
    const updatedSections = Sections.map(section => {
      if (section.key === sectionKey) {
        section.blocks = section.blocks.map(block => {
          if (block.title === oldTitle) {
            return { ...block, title: newTitle };
          }
          return block;
        });
      }
      return section;
    });

    setSections(updatedSections);
  }

  function changeMapToDefault(sectionTitle, sectionKey) {
    const updatedSections = Sections.map(section => {
      if (section.key === sectionKey) {
        section.mapBlock = null;
        section.title = sectionTitle;
      }
      return section;
    });

    setSections(updatedSections);
  }

  function updatedBlockPrefixAndSuffix(
    blockTitle,
    sectionKey,
    prefix = null,
    suffix = null,
  ) {
    const updatedSections = Sections.map(section => {
      if (section.key === sectionKey) {
        section.blocks = section.blocks.map(block => {
          if (block.title === blockTitle) {
            return { ...block, prefix, suffix };
          }
          return block;
        });
      }
      return section;
    });

    // take me out
    setSections(updatedSections);
  }

  function updateBlockValue(blockTitle, sectionKey, value, group = null) {
    const updatedSections = Sections.map(section => {
      if (section.key === sectionKey) {
        section.blocks = section.blocks.map(block => {
          if (block.title === blockTitle && block.group === group) {
            return { ...block, templateValue: value };
          }
          return block;
        });
      }
      return section;
    });

    // take me out
    setSections(updatedSections);
  }

  function clearStates() {
    setSections(JSON.parse(JSON.stringify(sectionsJson)));
    setEditing(false);
    setTemplateID(null);
    setNewTemplateName('');
    setLastStage('createTemplate');
    setCurrentStage('templateTable');
  }

  async function saveTemplate() {
    try {
      let customSections = Sections.map(section => {
        const tempSection = {};
        if (!section.status) return undefined;

        tempSection.key = section.key;
        tempSection.dynamicBlocks = [];

        if (section.map.status || section.status) tempSection.map = '';
        else return undefined;

        tempSection.webmapID = section.map.webmapID;
        tempSection.mapBlock = section.mapBlock;
        tempSection.title = section.title;

        // Include custom map configuration if it exists
        if (section.mapBlock?.customMapConfig) {
          tempSection.mapBlock.customMapConfig =
            section.mapBlock.customMapConfig;
        }

        tempSection.dynamicBlocks = section.blocks
          .filter(block => block.status)
          .map(block => ({
            title: block.title,

            outfields: block.outfields || block.outFields,
            serviceURL: block.serviceURL || null,
            prefix: block.prefix,
            suffix: block.suffix,
            group: block?.group || '',
            templateValue: block?.templateValue || '',
            value: '',
            key: section.key,
            layout: 'l1',
          }));

        return tempSection;
      });
      customSections = customSections.filter(Boolean);

      const user = JSON.parse(localStorage.getItem('mongodbUser'));
      if (!user) throw new Error('User not found in localstorage');

      const template = {
        templateName: newTemplateName,
        customSections,

        ...(editing === false && {
          userID: user._id,
          theme: colors[Math.floor(Math.random() * colors.length)],
        }),
      };

      // return;
      let url;
      if (editing)
        url = process.env.REACT_APP_NODE_URL + '/templates/' + templateID;
      else url = process.env.REACT_APP_NODE_URL + '/templates/add';

      const res = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',

          Authorization: `Bearer ${getAccessToken()}`,
        },
        body: JSON.stringify(template),
      });

      if (!res.ok) throw new Error('Endpoint Response Error');
      clearStates();
    } catch (error) {
      console.error(error);
    }
  }

  async function loadTemplate(template) {
    try {
      const { templateName, sections } = template;

      setNewTemplateName(templateName);

      const updatedSections = [...Sections]; // Create a copy of the current sections

      sections.forEach(loadedSection => {
        const existingSection = updatedSections.find(
          section => section.key === loadedSection.key,
        );

        if (existingSection) {
          // Update existing section
          existingSection.status = true;
          existingSection.map.status = true;
          existingSection.mapBlock = loadedSection.mapBlock || null;
          existingSection.title = loadedSection?.title || existingSection.title;

          // Restore custom map configuration if it exists
          if (loadedSection.mapBlock?.customMapConfig) {
            existingSection.mapBlock.customMapConfig =
              loadedSection.mapBlock.customMapConfig;
          }

          if (loadedSection.dynamicBlocks.length > 0) {
            existingSection.blocks = existingSection.blocks.map(block => {
              const loadedBlock = loadedSection.dynamicBlocks.find(
                dynamicBlock => dynamicBlock.key === block.key,
              );
              block.status = loadedBlock ? true : false;
              return block;
            });

            loadedSection.dynamicBlocks.forEach(dynamicBlock => {
              const existingBlock = existingSection.blocks.find(
                block =>
                  block.title === dynamicBlock.title &&
                  block?.group === dynamicBlock?.group,
              );
              if (!existingBlock) {
                existingSection.blocks.push({ ...dynamicBlock, status: true });
              }
            });
          }
        } else {
          // Add new section
          updatedSections.push({
            ...loadedSection,
            status: true,
            map: {
              ...loadedSection.map,
              status: true,
              webmapID: loadedSection?.webmapID,
              key: loadedSection?.key,
            },
            blocks: loadedSection.dynamicBlocks.map(block => ({
              ...block,
              status: true,
            })),
          });
        }
      });

      // Update the state with the modified sections
      setSections(updatedSections);
      setEditing(true);
      setTemplateID(template._id);
      setNewTemplateName(template.templateName);
      setSections(updatedSections);
      setCurrentStage('createTemplate');
      setLastStage('saveAndFinish');
    } catch (error) {
      console.error('Error loading template:', error);
    }
  }

  async function getToken() {
    try {
      const accessToken = await getAccessToken();
      const url = process.env.REACT_APP_NODE_URL + '/admin/token';
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (!response.ok) throw new Error('Response error');

      const data = await response.json();
      if (data?.token) {
        setArcgisToken(data.token.access_token);
        return data.token.access_token;
      }
      throw new Error('Token not found');
    } catch (error) {
      console.error('Error getting token');
      console.error(error);
    }
  }

  async function getBlocksFromArcGIS(sectionKey) {
    try {
      const token = await fetchToken();

      const map = Maps.find(map => map.key === sectionKey);
      if (!map) {
        throw new Error(`Map with key ${sectionKey} not found`);
      }

      const layerUrlsAndTitles = await fetchLayerUrlsAndTitles(
        map.webmapID,
        token,
      );

      const layerUrls = layerUrlsAndTitles.map(item => item.url);
      const newBlocks = await fetchFields(layerUrls, token);

      updateSections(sectionKey, newBlocks);
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  }

  async function fetchToken() {
    if (!arcgisToken) {
      return await getToken();
    }
    return arcgisToken;
  }

  async function fetchLayerUrlsAndTitles(webmapID, token) {
    const layerFetchingUrl = `https://www.arcgis.com/sharing/rest/content/items/${webmapID}/data`;
    const layerFetchingParams = new URLSearchParams({ f: 'pjson', token });
    const response = await fetch(
      `${layerFetchingUrl}?${layerFetchingParams.toString()}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      },
    );

    if (!response.ok) {
      throw new Error("Couldn't fetch the layers");
    }

    const data = await response.json();
    if (!data.operationalLayers) {
      throw new Error('Operational layers not found in the webmap');
    }

    const layersInfo = [];
    data.operationalLayers.forEach(operationalLayer => {
      if (operationalLayer?.url) {
        const baseLayerInfo = {
          url: operationalLayer.url,
          title: operationalLayer.title || 'No title available',
        };

        layersInfo.push(baseLayerInfo);

        if (operationalLayer.visibleLayers) {
          const finalPart =
            operationalLayer.url.split('/')[
              operationalLayer.url.split('/').length - 1
            ];
          if (!+finalPart) {
            operationalLayer.visibleLayers.forEach(layer => {
              const visibleLayerInfo = {
                url: `${operationalLayer.url}/${layer}`,
                title: operationalLayer.title || `Layer ${layer}`,
              };
              layersInfo.push(visibleLayerInfo);
            });
          }
        }
      }

      if (operationalLayer?.layers) {
        operationalLayer.layers.forEach(layer => {
          if (layer?.url) {
            const layerInfo = {
              url: layer.url,
              title: layer.title || 'No title available',
            };
            layersInfo.push(layerInfo);
          }
        });
      }
    });

    return layersInfo;
  }

  async function fetchFields(layerUrls, token) {
    const fieldPromises = layerUrls.map(async url => {
      try {
        const response = await fetch(`${url}?f=pjson&token=${token}`);
        if (!response.ok) {
          throw new Error(`Failed to fetch data from ${url}`);
        }
        const data = await response.json();
        return {
          name: data.name || '',
          fields: data.fields || [],
          serviceURL: url,
        };
      } catch (error) {
        console.error(`Error fetching fields from ${url}:`, error);
        return {
          name: '',
          fields: [],
          serviceURL: null,
        };
      }
    });

    const fieldsResponse = await Promise.all(fieldPromises);

    fieldsResponse.forEach(fieldObject => {
      const filteredFields = fieldObject.fields.filter(
        field => !field.alias.includes('2028'),
      );
      fieldObject.fields = filteredFields;
    });

    const uniqueFields = [];
    const fieldNames = new Set();

    fieldsResponse.forEach(fieldObj => {
      fieldObj.fields.forEach(field => {
        if (!fieldNames.has(field.name)) {
          fieldNames.add(field.name);
          uniqueFields.push({
            group: fieldObj.name,
            title: field.alias,
            status: false,
            type: field.type,
            prefix: null,
            suffix: null,
            outfields: [field.name],
            serviceURL: fieldObj.serviceURL,
          });
        }
      });
    });

    return uniqueFields;
  }

  function updateSections(sectionKey, newBlocks) {
    const updatedSections = Sections.map(sec => {
      if (sec.key === sectionKey) {
        // Get existing blocks from sectionsJson
        const existingBlocks =
          sectionsJson.find(s => sectionKey.includes(s.key))?.blocks || [];
        // Combine existing blocks, new blocks, and blocks from sectionsJson
        const combinedBlocks = [
          ...(sec.blocks || []).filter(block => block.status === true),
          ...newBlocks,
          ...existingBlocks.filter(
            block =>
              !sec.blocks.some(
                b => b.title === block.title && b.status === true,
              ),
          ),
        ];

        // Remove duplicates
        const uniqueBlocks = Array.from(
          new Set(combinedBlocks.map(JSON.stringify)),
        ).map(JSON.parse);

        sec.blocksFetched = true;
        return { ...sec, blocks: uniqueBlocks };
      }
      return sec;
    });

    setSections(updatedSections);
  }

  async function fetchMaps() {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_NODE_URL}/map/templates`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAccessToken()}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error('Failed to fetch maps for templates');
      }

      const data = await response.json();

      if (data?.status === true) {
        const fetchedMaps = data?.maps;
        const updatedSections = fetchedMaps.map(map => {
          const sectionToUpdate = Sections.find(
            section => section.key === map.key,
          );
          if (sectionToUpdate) {
            return {
              ...sectionToUpdate,
              map: {
                ...map,
                image: map?.imageURL,
                status: sectionToUpdate?.map?.status ? true : false,
              },
            };
          } else {
            return {
              key: map.key,
              title: map.title,
              blocks: [],
              map: {
                ...map,
                image: map?.imageURL,
                status: false,
              },
            };
          }
        });

        setSections(updatedSections);
        setMaps(data.maps);
        return updatedSections;
      }
    } catch (error) {
      console.error(error);
    }
  }

  async function adminCheck() {
    try {
      const email = mongoUser?.email || user?.Email;
      if (!email) {
        // Auth not ready; skip for now
        return;
      }
      const response = await fetch(
        process.env.REACT_APP_NODE_URL + '/admin/check-admin',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAccessToken()}`,
          },
          body: JSON.stringify({ email }),
        },
      );

      if (!response.ok) throw new Error('Failed to check admin status');

      const data = await response.json();
      if (data.isAdmin === false) {
        return navigate('/run-reports');
      }
    } catch (error) {
      console.error(error);
      // Do not redirect on transient auth readiness issues
    } finally {
      setAdminLoader(false);
    }
  }

  useEffect(() => {
    if (!isLoading) {
      adminCheck().then(() => {
        fetchMaps();
      });
    }
  }, [isLoading, user, mongoUser]);

  return (
    <ReportTemplatesContext.Provider
      value={{
        setCurrentStage,
        currentStage,
        newTemplateName,
        setNewTemplateName,
        setSections,
        Sections,
        lastStage,
        setLastStage,
        addBlockModal,
        setAddBlockModal,
        removeBlock,
        saveTemplate,
        loadTemplate,
        editing,
        setEditing,
        getAccessToken,
        getBlocksFromArcGIS,
        setLoading,
        fetchMaps,
        Maps,
        updateBlockTitle,
        updatedBlockPrefixAndSuffix,
        getToken,
        fetchToken,
        fetchLayerUrlsAndTitles,
        fetchFields,
        updateSections,
        updateBlockValue,
        addCustomMapInfo,
        changeMapToDefault,
        customMapData,
        setCustomMapData,
        handleSaveCustomMap,
      }}
    >
      {adminLoader === true ? (
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            height: '100%',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {/* Header */}

          <Box
            sx={{
              paddingX: {
                md: '1.25rem',
                xs: '0',
              },
              marginBottom: '1.25rem',
            }}
          >
            <Header
              sideBarState={setSideBarState}
              pageTitle={
                {
                  templateTable: 'Templates',
                }[currentStage] || 'Add New Templates'
              }
            />
            <Box
              sx={{
                paddingX: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '.15rem',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Lato',
                  fontSize: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  fontWeight: 800,
                  lineHeight: '24px',
                  letterSpacing: '0.2px',
                  textAlign: 'left',
                  color: '#004852',
                  cursor: 'pointer',
                }}
                onClick={clearStates}
              >
                Template Table
                <GoBack />
              </Typography>
            </Box>
          </Box>

          {/* Main Content */}
          <Box
            sx={{
              paddingX: {
                md: '1.25rem',
                xs: '0',
              },
              display: 'flex',
              flexDirection: 'column',
              gap: '1.25rem',
            }}
          >
            {currentStage !== 'templateTable' && <ProgressIndicator />}
            {{
              templateTable: <TemplateTableSection />,
              createTemplate: <CreateTemplateSection />,
              customizeTemplate: <CustomizeTemplateSection />,
              saveAndFinish: <CustomizeTemplateSection />,
            }[currentStage] || <TemplateTableSection />}
          </Box>
        </Box>
      )}
    </ReportTemplatesContext.Provider>
  );
}
